/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import type { AgentCapabilities } from '@/blackbolt/agent/transport'
import type { ChatKey } from '@/blackbolt/chat/i18n/types.generated'
import { FireflyApiKeys } from '@/service/gql'

// ── Connection ──────────────────────────────────────────────────────────────

/**
 * Resonance ALPHA host — the primary dev stage where agent updates land first
 * (prod resonance isn't reachable yet). Maps to `AgentEndpoint.ALPHA` in the
 * KMP `RewindApp` client.
 */
export const AGENT_LIVE_HOST = 'https://alpha.resonance.music.amazon.dev'

/** Vite dev proxy prefix — dodges CORS in local dev. */
export const AGENT_PROXY_PATH = '/proxy/agents'

// ── API identity ────────────────────────────────────────────────────────────

/** BlackBoltWeb API key — identifies this client to the resonance gateway. */
export const AGENT_API_KEY = FireflyApiKeys.FIREFLY_BLACKBOLT_WEB_API_KEY

/** `X-Map-App-Name` — Bullfrog shares RewindApp's app-level identity. */
export const AGENT_APP_NAME = 'RewindApp'

// ── WebSocket query params ──────────────────────────────────────────────────

export const WS_CHANNEL = 'interactive'
export const WS_CAPABILITIES = 'cards'

// ── Capabilities ────────────────────────────────────────────────────────────

export const CLAW_CAPABILITIES: AgentCapabilities = {
    richCards: true,
    toolActivity: true,
    suggestions: true,
    imageInput: true,
    cardHydration: true,
}

// ── Tool label map ──────────────────────────────────────────────────────────

// Claw wire tool name to bundle key for the bare activity phrase shown while
// a tool runs (iOS friendlyLabel parity).
export const TOOL_LABEL_KEYS = {
    search_catalog: 'dmusic_chat_tool_label_search_catalog',
    create_playlist: 'dmusic_chat_tool_label_create_playlist',
    create_record: 'dmusic_chat_tool_label_create_record',
    get_recommendations: 'dmusic_chat_tool_label_get_recommendations',
    get_recently_played: 'dmusic_chat_tool_label_get_recently_played',
    get_top_tracks: 'dmusic_chat_tool_label_get_top_tracks',
    get_library: 'dmusic_chat_tool_label_get_library',
    ask_amazon_music: 'dmusic_chat_tool_label_ask_amazon_music',
    memory_search: 'dmusic_chat_tool_label_memory_search',
    get_current_user_recently_played_tracks:
        'dmusic_chat_tool_label_get_current_user_recently_played_tracks',
    append_tracks_to_playlist: 'dmusic_chat_tool_label_append_tracks_to_playlist',
    get_playlist: 'dmusic_chat_tool_label_get_playlist',
    read: 'dmusic_chat_tool_label_read',
    due_following: 'dmusic_chat_tool_label_due_following',
} as const satisfies Readonly<Partial<Record<string, ChatKey>>>

export type ToolKey = keyof typeof TOOL_LABEL_KEYS
